import { useState, useEffect, useCallback } from 'react';

let cachedMembership: Membership | null | undefined;
let cachedResolved = false;

export function __resetMembershipCacheForTests() {
  cachedMembership = undefined;
  cachedResolved = false;
}

export interface Membership {
  userId: string;
  programmeId: string;
  status: string;
  joinedAt: string;
}

interface UseMembershipResult {
  membership: Membership | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch and cache the current user's membership status.
 * Returns null for membership if user is not a member (404 response).
 */
export function useMembership(): UseMembershipResult {
  const [membership, setMembership] = useState<Membership | null>(
    cachedResolved ? cachedMembership ?? null : null
  );
  const [loading, setLoading] = useState(!cachedResolved);
  const [error, setError] = useState<string | null>(null);

  const fetchMembership = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/memberships/me');

      if (response.status === 404) {
        // User is not a member - this is a valid state, not an error
        setMembership(null);
        cachedMembership = null;
        cachedResolved = true;
        setLoading(false);
        return;
      }

      if (response.status === 401) {
        // Not authenticated - also a valid state
        setMembership(null);
        cachedMembership = null;
        cachedResolved = true;
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch membership status');
      }

      const data: Membership = await response.json();
      setMembership(data);
      cachedMembership = data;
      cachedResolved = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setMembership(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (cachedResolved) {
      setMembership(cachedMembership ?? null);
      setLoading(false);
      return;
    }
    fetchMembership();
  }, [fetchMembership]);

  return {
    membership,
    loading,
    error,
    refetch: fetchMembership,
  };
}
